package ai.wagemix.web.screens.marketing

import ai.wagemix.logic.marketing.WMMarketingChromePresenter
import ai.wagemix.logic.marketing.WMMarketingChromePresenter.Action
import ai.wagemix.logic.marketing.WMMarketingChromePresenter.Event
import ai.wagemix.net.models.WMSession
import ai.wagemix.web.components.WMButton
import ai.wagemix.web.components.WMButtonKind
import ai.wagemix.web.components.WMButtonSize
import ai.wagemix.web.screens.WMWebStrings
import androidx.compose.runtime.Composable
import androidx.compose.runtime.State
import com.fsryan.foundation.web.screens.FSWebScreen

/**
 * Binds the marketing chrome's presenter to the site's marketing `@Layout`: [content] renders the
 * whole chrome and receives the session actions to put into the header. [leaveWhenSignedIn] is the
 * key, so the landing page and the other marketing pages each get their own view model.
 */
@Composable
fun <
    E: Event,
    A: Action,
    PR: WMMarketingChromePresenter<E, A>,
    VM: WMBaseMarketingChromeWebViewModel<E, A, PR>
> WMMarketingChromeScreen(
    createViewModel: () -> VM,
    leaveWhenSignedIn: Boolean,
    strings: WMWebStrings,
    onSignIn: () -> Unit,
    onRegister: () -> Unit,
    /** Told when "Open app" is clicked, before the presenter navigates; the site reports the click. */
    onOpenAppClick: () -> Unit = {},
    content: @Composable (sessionActions: @Composable () -> Unit) -> Unit
) {
    FSWebScreen(createViewModel = createViewModel, key = leaveWhenSignedIn) { vm ->
        content {
            WMMarketingSessionActionsLayout(
                sessionResolvedState = vm.sessionResolvedState,
                sessionState = vm.sessionState,
                strings = strings,
                onSignIn = onSignIn,
                onRegister = onRegister,
                onOpenApp = {
                    onOpenAppClick()
                    vm.userRequestedOpenApp()
                }
            )
        }
    }
}

/**
 * The header's session actions: nothing until the session is known (so the exported page and a
 * returning user never see the wrong pair), Log in / Create account for a visitor, Open app when
 * signed in.
 */
@Composable
fun WMMarketingSessionActionsLayout(
    sessionResolvedState: State<Boolean>,
    sessionState: State<WMSession?>,
    strings: WMWebStrings,
    onSignIn: () -> Unit,
    onRegister: () -> Unit,
    onOpenApp: () -> Unit
) {
    if (!sessionResolvedState.value) {
        return
    }
    if (sessionState.value != null) {
        WMButton(strings.common.openApp, size = WMButtonSize.Sm, onClick = onOpenApp)
        return
    }
    WMButton(strings.common.signIn, kind = WMButtonKind.Ghost, size = WMButtonSize.Sm, onClick = onSignIn)
    WMButton(strings.common.register, size = WMButtonSize.Sm, onClick = onRegister)
}
